import { Box } from '@mui/material'
import { GridColDef } from '@mui/x-data-grid-premium'
import { Table } from 'Common/MUITableV2'
import { autoHeight } from 'Common/utils/utils'

/**
 * The grid, in its loading state, with the columns it is about to show.
 *
 * MUI X v8 already ships this: a `<Table loading>` with no rows picks
 * `loadingOverlay`'s `noRowsVariant`, which defaults to `'skeleton'` and
 * draws shimmering cells at the real column widths. The input tables get it
 * for free through `BaseTable`, which passes `loading` straight to the grid.
 * The utilisation views did not — each returned a centred `CircularProgress`
 * BEFORE rendering its table, so the grid never mounted and its skeleton
 * never had the chance to appear. This is the same early return, showing the
 * grid instead of a spinner.
 *
 * Two details are what make it line up with the loaded table rather than
 * approximate it:
 *
 * - **The real `columns`.** Skeleton cells are laid out from the column
 *   definitions, so passing them means the placeholder has the same column
 *   count and widths as the data that replaces it — no reflow on arrival.
 * - **The same `autoHeight` ref.** `.table-container-mui` carries no CSS
 *   rule; it exists only for that helper to set an inline height on, derived
 *   from the element's position in the window (min 300px). Reusing it gives
 *   the placeholder the height the real table will take. It also matters for
 *   a reason that is easy to miss: the skeleton renders
 *   `ceil(viewportHeight / rowHeight)` rows, so a grid with no resolved
 *   height draws NO skeleton rows at all and the whole thing looks like an
 *   empty box. Anything using the grid's own `autoHeight` prop instead needs
 *   a `minHeight` for the same reason.
 */
export const GridSkeleton = ({
  columns,
  density = 'compact',
}: {
  columns: GridColDef[]
  density?: 'compact' | 'standard' | 'comfortable'
}) => (
  <Box className="table-container-mui">
    <Table
      // Block body, not `el && autoHeight(el)`: React 19 types a ref
      // callback as returning void (a returned value is treated as a
      // cleanup function), and the && form returns null when el is null.
      // The surrounding views still use the expression form and each carries
      // this same pre-existing type error; not fixing those here.
      ref={(el: HTMLDivElement | null) => {
        if (el) autoHeight(el)
      }}
      rows={[]}
      columns={columns}
      loading
      density={density}
      hideFooter
      disableRowSelectionOnClick
    />
  </Box>
)
